// Orders + payment (docs/API.md §4). Payment capture is NEVER in-app — Pakasir hosts the
// QRIS checkout; the dashboard creates the order, renders the frozen QRIS and polls status.
//
// `POST /orders` and `GET /coupons/:code` answer **410 sales_closed** whenever the API runs
// with SALES_ENABLED=false (its default in every environment). That is a normal, expected
// answer here, not an outage: callers render it as a plain "not on sale" state and never retry.

import { apiGet, apiPost } from "@/lib/api";
import type {
  CouponPreview,
  CreateOrderResponse,
  Order,
  OrderRaw,
  OrderStatus,
  PaidTierId,
} from "@/lib/types";

/** sessionStorage key for a `?coupon=` deep link stashed before login (mirrors
 *  auth.tsx's `ahcut.ref` — same origin/tab survives the OAuth redirect round trip).
 *  Single source of truth so auth.tsx (writer) and the Buy page (reader) never drift. */
export const COUPON_SESSION_KEY = "ahcut.coupon";

function normalizeOrder(raw: OrderRaw): Order {
  return {
    id: raw.id ?? raw.order_id ?? "",
    tier: raw.tier,
    amount: raw.amount,
    currency: raw.currency,
    status: raw.status,
    created_at: raw.created_at ?? null,
    expires_at: raw.expires_at ?? null,
    fee: raw.fee ?? null,
    total_payment: raw.total_payment ?? null,
    checkout: raw.checkout,
    license_id: raw.license_id ?? null,
    coupon_code: raw.coupon_code ?? null,
    list_amount: raw.list_amount ?? null,
    provider_txn_id: raw.provider_txn_id ?? null,
    paid_at: raw.paid_at ?? null,
  };
}

/**
 * The amount the QR actually bills. Fee-by-buyer projects charge
 * total_payment = amount + fee; either field may be absent, so derive the
 * missing one. Falls back to the plain amount when no fee was reported.
 */
export function orderBilledTotal(o: Pick<Order, "amount" | "fee" | "total_payment">): {
  total: number;
  fee: number | null;
} {
  if (o.total_payment && o.total_payment > o.amount) {
    return { total: o.total_payment, fee: o.fee ?? o.total_payment - o.amount };
  }
  if (o.fee && o.fee > 0) {
    return { total: o.amount + o.fee, fee: o.fee };
  }
  return { total: o.amount, fee: null };
}

/** Frozen price lines of an order, shared by the checkout view and the receipt. */
export interface OrderPriceBreakdown {
  /** Pre-discount price (list_amount; the amount itself on legacy rows). */
  list: number;
  /** Coupon discount (0 without a coupon, never negative). `amount` is already discounted. */
  discount: number;
  /** QRIS fee billed on top of `amount`, null when unreported. */
  fee: number | null;
  /** What the buyer's bank app bills: amount + fee. */
  total: number;
}

export function orderPriceBreakdown(
  o: Pick<Order, "amount" | "fee" | "total_payment" | "coupon_code" | "list_amount">,
): OrderPriceBreakdown {
  const { total, fee } = orderBilledTotal(o);
  const list = o.list_amount ?? o.amount;
  const discount = o.coupon_code ? Math.max(0, list - o.amount) : 0;
  return { list, discount, fee, total };
}

/**
 * Mint one Idempotency-Key per CHECKOUT INTENT (first click), and reuse it until
 * a CreateOrderResponse arrives — so a retry after a timeout returns the SAME
 * order instead of minting an orphan charge (docs/API.md §0). A coupon change is
 * a NEW intent (a different charge amount), hence the coupon is part of the key.
 */
export function newIdempotencyKey(tier: PaidTierId, coupon?: string): string {
  return `ord-${tier}-${coupon || "none"}-${crypto.randomUUID()}`;
}

/** Create a purchase for a paid tier. The server recomputes the amount from
 *  /pricing and re-validates the coupon (the client preview is advisory only).
 *  Throws `ApiError`: 410 `sales_closed`, 409 `conflict` (already entitled),
 *  422 `coupon_invalid`, 502 `payment_failed`. */
export function createOrder(
  tier: PaidTierId,
  idempotencyKey: string,
  coupon?: string,
): Promise<CreateOrderResponse> {
  const body: { tier: PaidTierId; coupon?: string } = { tier };
  if (coupon) body.coupon = coupon;
  return apiPost<CreateOrderResponse>("/orders", body, {
    headers: { "Idempotency-Key": idempotencyKey },
  });
}

/**
 * Pre-checkout coupon preview (GET /coupons/{code}?tier=…) — validates against
 * the tier's list price WITHOUT creating an order, so the Buy page can show the
 * struck-through price before the buyer commits. Throws `ApiError` (422
 * `coupon_invalid`, id-ID message) on a rejection — the caller displays it verbatim —
 * and 410 `sales_closed` when new sales are switched off.
 */
export function previewCoupon(
  code: string,
  tier: PaidTierId,
  signal?: AbortSignal,
): Promise<CouponPreview> {
  return apiGet<CouponPreview>(`/coupons/${encodeURIComponent(code)}?tier=${tier}`, { signal });
}

export async function listOrders(signal?: AbortSignal): Promise<Order[]> {
  const raw = await apiGet<OrderRaw[]>("/orders", { signal });
  return (raw ?? []).map(normalizeOrder);
}

export async function getOrder(id: string, signal?: AbortSignal): Promise<Order> {
  const raw = await apiGet<OrderRaw>(`/orders/${encodeURIComponent(id)}`, { signal });
  return normalizeOrder(raw);
}

/**
 * True when the order carries something to pay with: a stored QRIS payload or the
 * hosted checkout link. Pre-0005 orders and the pending orders of an already-entitled
 * user (server-withheld, API.md §4) carry neither.
 */
export function orderHasPaymentDetails(o: Pick<Order, "checkout">): boolean {
  return Boolean(o.checkout?.qris_payload || o.checkout?.checkout_url);
}

/**
 * True when the provider-reported charge expiry has passed — the QRIS can no
 * longer be paid even if the row still says `pending` (the server maintenance
 * loop flips it after a settlement grace). Unknown/absent expiry never counts as expired.
 */
export function orderPastExpiry(o: Pick<Order, "expires_at">): boolean {
  if (!o.expires_at) return false;
  const t = new Date(o.expires_at).getTime();
  return !Number.isNaN(t) && t <= Date.now();
}

/**
 * The status to DISPLAY in lists and receipts: a pending order past its QRIS
 * deadline reads Kedaluwarsa immediately, before the server flips the row, so a
 * dead QRIS never looks payable.
 */
export function orderDisplayStatus(o: Pick<Order, "status" | "expires_at">): OrderStatus {
  return o.status === "pending" && orderPastExpiry(o) ? "expired" : o.status;
}
